/**
 * Registre ANGLAIS des pages chauffeur dynamiques + jointure FR↔EN.
 *
 * Le contenu EN vit dans slugs-en/ (un fichier par page, < 400 lignes), typé
 * `ChauffeurPage` exactement comme le FR → rendu par la vue partagée
 * ChauffeurSlugView. La structure FR reste la source (slug-chauffeur.ts) ;
 * ici on n'ajoute que la traduction + la table de correspondance des slugs.
 *
 * Slugs EN choisis pour être keyword-rich ET sans collision avec les hubs
 * déjà publiés (/en/chauffeur/transfers, /en/chauffeur/partners).
 */
import type { ChauffeurPage } from "./slug-chauffeur";
import servicesEn from "./slugs-en/services";
import cannesEn from "./slugs-en/cannes";
import frenchRivieraEn from "./slugs-en/french-riviera";
import eventsEn from "./slugs-en/events";
import businessEn from "./slugs-en/business";
import airportEn from "./slugs-en/airport";
import niceEn from "./slugs-en/nice";
import monacoEn from "./slugs-en/monaco";
import antibesEn from "./slugs-en/antibes";
import saintTropezEn from "./slugs-en/saint-tropez";
import cannesFilmFestivalEn from "./slugs-en/cannes-film-festival";
import mipimEn from "./slugs-en/mipim";
import mipcomEn from "./slugs-en/mipcom";
import cannesLionsEn from "./slugs-en/cannes-lions";
import monacoGrandPrixEn from "./slugs-en/monaco-grand-prix";
import monacoYachtShowEn from "./slugs-en/monaco-yacht-show";
import saintJeanCapFerratEn from "./slugs-en/saint-jean-cap-ferrat";
import ezeEn from "./slugs-en/eze";
import villefrancheEn from "./slugs-en/villefranche-sur-mer";
import mouginsEn from "./slugs-en/mougins";
import sophiaAntipolisEn from "./slugs-en/sophia-antipolis";
import cannesYachtingFestivalEn from "./slugs-en/cannes-yachting-festival";
import tfwaCannesEn from "./slugs-en/tfwa-cannes";
import monteCarloMastersEn from "./slugs-en/monte-carlo-masters";
import iltmCannesEn from "./slugs-en/iltm-cannes";
import niceCarnivalEn from "./slugs-en/nice-carnival";
import securityChauffeurEn from "./slugs-en/security-chauffeur";
import vipMinibusEn from "./slugs-en/vip-minibus";
import sedanWithChauffeurEn from "./slugs-en/sedan-with-chauffeur";
import sClassWithChauffeurEn from "./slugs-en/s-class-with-chauffeur";
import vanWithChauffeurEn from "./slugs-en/van-with-chauffeur";
import suvWithChauffeurEn from "./slugs-en/suv-with-chauffeur";

// Villes Riviera — expansion locality (EN slug = FR slug).
import mandelieuLaNapouleEn from "./slugs-en/mandelieu-la-napoule";
import leCannetEn from "./slugs-en/le-cannet";
import vallaurisGolfeJuanEn from "./slugs-en/vallauris-golfe-juan";
import juanLesPinsEn from "./slugs-en/juan-les-pins";
import theouleSurMerEn from "./slugs-en/theoule-sur-mer";
import grasseEn from "./slugs-en/grasse";
import valbonneEn from "./slugs-en/valbonne";
import biotEn from "./slugs-en/biot";
import villeneuveLoubetEn from "./slugs-en/villeneuve-loubet";
import cagnesSurMerEn from "./slugs-en/cagnes-sur-mer";
import saintLaurentDuVarEn from "./slugs-en/saint-laurent-du-var";
import saintPaulDeVenceEn from "./slugs-en/saint-paul-de-vence";
import venceEn from "./slugs-en/vence";
import beaulieuSurMerEn from "./slugs-en/beaulieu-sur-mer";
import capDAilEn from "./slugs-en/cap-d-ail";
import beausoleilEn from "./slugs-en/beausoleil";
import mentonEn from "./slugs-en/menton";
import roquebruneCapMartinEn from "./slugs-en/roquebrune-cap-martin";
import laTurbieEn from "./slugs-en/la-turbie";
import tourrettesSurLoupEn from "./slugs-en/tourrettes-sur-loup";
import laColleSurLoupEn from "./slugs-en/la-colle-sur-loup";
import mouansSartouxEn from "./slugs-en/mouans-sartoux";
import opioEn from "./slugs-en/opio";
import roquefortLesPinsEn from "./slugs-en/roquefort-les-pins";
import saintRaphaelEn from "./slugs-en/saint-raphael";
import frejusEn from "./slugs-en/frejus";
import sainteMaximeEn from "./slugs-en/sainte-maxime";
import grimaudEn from "./slugs-en/grimaud";
import portGrimaudEn from "./slugs-en/port-grimaud";
import cogolinEn from "./slugs-en/cogolin";
import gassinEn from "./slugs-en/gassin";
import laCroixValmerEn from "./slugs-en/la-croix-valmer";
import ramatuelleEn from "./slugs-en/ramatuelle";
import cavalaireSurMerEn from "./slugs-en/cavalaire-sur-mer";
import leLavandouEn from "./slugs-en/le-lavandou";
import bormesLesMimosasEn from "./slugs-en/bormes-les-mimosas";
import hyeresEn from "./slugs-en/hyeres";
import toulonEn from "./slugs-en/toulon";

// Événements Riviera (EN slug = FR slug).
import niceJazzFestivalEn from "./slugs-en/nice-jazz-festival";
import jazzAJuanEn from "./slugs-en/jazz-a-juan";
import lesVoilesDeSaintTropezEn from "./slugs-en/les-voiles-de-saint-tropez";
import feteDuCitronMentonEn from "./slugs-en/fete-du-citron-menton";
import regatesRoyalesCannesEn from "./slugs-en/regates-royales-cannes";
import ironmanFranceNiceEn from "./slugs-en/ironman-france-nice";
import jumpingInternationalMonacoEn from "./slugs-en/jumping-international-monaco";
import monacoEPrixEn from "./slugs-en/monaco-e-prix";
import mapicCannesEn from "./slugs-en/mapic-cannes";
import canneseriesEn from "./slugs-en/canneseries";
import feteDuJasminGrasseEn from "./slugs-en/fete-du-jasmin-grasse";
import giragliaSaintTropezEn from "./slugs-en/giraglia-saint-tropez";
import weddingCarCannesEn from "./slugs-en/wedding-car-cannes";

/** Jointure stable FR↔EN : dérive URLs, canonicals et hreflang des deux côtés. */
export const CHAUFFEUR_SLUG_MAP: { fr: string; en: string }[] = [
  { fr: "services", en: "services" },
  { fr: "cannes", en: "cannes" },
  { fr: "cote-d-azur", en: "french-riviera" },
  { fr: "evenements", en: "events" },
  { fr: "entreprise", en: "business" },
  { fr: "aeroport", en: "airport" },
  { fr: "nice", en: "nice" },
  { fr: "monaco", en: "monaco" },
  { fr: "antibes", en: "antibes" },
  { fr: "saint-tropez", en: "saint-tropez" },
  { fr: "festival-de-cannes", en: "cannes-film-festival" },
  { fr: "mipim", en: "mipim" },
  { fr: "mipcom", en: "mipcom" },
  { fr: "cannes-lions", en: "cannes-lions" },
  { fr: "grand-prix-de-monaco", en: "monaco-grand-prix" },
  { fr: "monaco-yacht-show", en: "monaco-yacht-show" },
  { fr: "saint-jean-cap-ferrat", en: "saint-jean-cap-ferrat" },
  { fr: "eze", en: "eze" },
  { fr: "villefranche-sur-mer", en: "villefranche-sur-mer" },
  { fr: "mougins", en: "mougins" },
  { fr: "sophia-antipolis", en: "sophia-antipolis" },
  { fr: "cannes-yachting-festival", en: "cannes-yachting-festival" },
  { fr: "tfwa-cannes", en: "tfwa-cannes" },
  { fr: "monte-carlo-masters", en: "monte-carlo-masters" },
  { fr: "iltm-cannes", en: "iltm-cannes" },
  { fr: "carnaval-de-nice", en: "nice-carnival" },
  { fr: "chauffeur-securite", en: "security-chauffeur" },
  { fr: "minibus-vip", en: "vip-minibus" },
  { fr: "berline-avec-chauffeur", en: "sedan-with-chauffeur" },
  { fr: "classe-s-avec-chauffeur", en: "s-class-with-chauffeur" },
  { fr: "van-avec-chauffeur", en: "van-with-chauffeur" },
  { fr: "suv-avec-chauffeur", en: "suv-with-chauffeur" },
  { fr: "mandelieu-la-napoule", en: "mandelieu-la-napoule" },
  { fr: "le-cannet", en: "le-cannet" },
  { fr: "vallauris-golfe-juan", en: "vallauris-golfe-juan" },
  { fr: "juan-les-pins", en: "juan-les-pins" },
  { fr: "theoule-sur-mer", en: "theoule-sur-mer" },
  { fr: "grasse", en: "grasse" },
  { fr: "valbonne", en: "valbonne" },
  { fr: "biot", en: "biot" },
  { fr: "villeneuve-loubet", en: "villeneuve-loubet" },
  { fr: "cagnes-sur-mer", en: "cagnes-sur-mer" },
  { fr: "saint-laurent-du-var", en: "saint-laurent-du-var" },
  { fr: "saint-paul-de-vence", en: "saint-paul-de-vence" },
  { fr: "vence", en: "vence" },
  { fr: "beaulieu-sur-mer", en: "beaulieu-sur-mer" },
  { fr: "cap-d-ail", en: "cap-d-ail" },
  { fr: "beausoleil", en: "beausoleil" },
  { fr: "menton", en: "menton" },
  { fr: "roquebrune-cap-martin", en: "roquebrune-cap-martin" },
  { fr: "la-turbie", en: "la-turbie" },
  { fr: "tourrettes-sur-loup", en: "tourrettes-sur-loup" },
  { fr: "la-colle-sur-loup", en: "la-colle-sur-loup" },
  { fr: "mouans-sartoux", en: "mouans-sartoux" },
  { fr: "opio", en: "opio" },
  { fr: "roquefort-les-pins", en: "roquefort-les-pins" },
  { fr: "saint-raphael", en: "saint-raphael" },
  { fr: "frejus", en: "frejus" },
  { fr: "sainte-maxime", en: "sainte-maxime" },
  { fr: "grimaud", en: "grimaud" },
  { fr: "port-grimaud", en: "port-grimaud" },
  { fr: "cogolin", en: "cogolin" },
  { fr: "gassin", en: "gassin" },
  { fr: "la-croix-valmer", en: "la-croix-valmer" },
  { fr: "ramatuelle", en: "ramatuelle" },
  { fr: "cavalaire-sur-mer", en: "cavalaire-sur-mer" },
  { fr: "le-lavandou", en: "le-lavandou" },
  { fr: "bormes-les-mimosas", en: "bormes-les-mimosas" },
  { fr: "hyeres", en: "hyeres" },
  { fr: "toulon", en: "toulon" },
  { fr: "nice-jazz-festival", en: "nice-jazz-festival" },
  { fr: "jazz-a-juan", en: "jazz-a-juan" },
  { fr: "les-voiles-de-saint-tropez", en: "les-voiles-de-saint-tropez" },
  { fr: "fete-du-citron-menton", en: "fete-du-citron-menton" },
  { fr: "regates-royales-cannes", en: "regates-royales-cannes" },
  { fr: "ironman-france-nice", en: "ironman-france-nice" },
  { fr: "jumping-international-monaco", en: "jumping-international-monaco" },
  { fr: "monaco-e-prix", en: "monaco-e-prix" },
  { fr: "mapic-cannes", en: "mapic-cannes" },
  { fr: "canneseries", en: "canneseries" },
  { fr: "fete-du-jasmin-grasse", en: "fete-du-jasmin-grasse" },
  { fr: "giraglia-saint-tropez", en: "giraglia-saint-tropez" },
  { fr: "voiture-mariage-cannes", en: "wedding-car-cannes" },
];

export const chauffeurPagesEn: ChauffeurPage[] = [
  servicesEn,
  cannesEn,
  frenchRivieraEn,
  eventsEn,
  businessEn,
  airportEn,
  niceEn,
  monacoEn,
  antibesEn,
  saintTropezEn,
  cannesFilmFestivalEn,
  mipimEn,
  mipcomEn,
  cannesLionsEn,
  monacoGrandPrixEn,
  monacoYachtShowEn,
  saintJeanCapFerratEn,
  ezeEn,
  villefrancheEn,
  mouginsEn,
  sophiaAntipolisEn,
  cannesYachtingFestivalEn,
  tfwaCannesEn,
  monteCarloMastersEn,
  iltmCannesEn,
  niceCarnivalEn,
  securityChauffeurEn,
  vipMinibusEn,
  sedanWithChauffeurEn,
  sClassWithChauffeurEn,
  vanWithChauffeurEn,
  suvWithChauffeurEn,
  mandelieuLaNapouleEn,
  leCannetEn,
  vallaurisGolfeJuanEn,
  juanLesPinsEn,
  theouleSurMerEn,
  grasseEn,
  valbonneEn,
  biotEn,
  villeneuveLoubetEn,
  cagnesSurMerEn,
  saintLaurentDuVarEn,
  saintPaulDeVenceEn,
  venceEn,
  beaulieuSurMerEn,
  capDAilEn,
  beausoleilEn,
  mentonEn,
  roquebruneCapMartinEn,
  laTurbieEn,
  tourrettesSurLoupEn,
  laColleSurLoupEn,
  mouansSartouxEn,
  opioEn,
  roquefortLesPinsEn,
  saintRaphaelEn,
  frejusEn,
  sainteMaximeEn,
  grimaudEn,
  portGrimaudEn,
  cogolinEn,
  gassinEn,
  laCroixValmerEn,
  ramatuelleEn,
  cavalaireSurMerEn,
  leLavandouEn,
  bormesLesMimosasEn,
  hyeresEn,
  toulonEn,
  niceJazzFestivalEn,
  jazzAJuanEn,
  lesVoilesDeSaintTropezEn,
  feteDuCitronMentonEn,
  regatesRoyalesCannesEn,
  ironmanFranceNiceEn,
  jumpingInternationalMonacoEn,
  monacoEPrixEn,
  mapicCannesEn,
  canneseriesEn,
  feteDuJasminGrasseEn,
  giragliaSaintTropezEn,
  weddingCarCannesEn,
];

/** Page EN par slug EN (ou undefined). */
export const getChauffeurPageEn = (enSlug: string): ChauffeurPage | undefined =>
  chauffeurPagesEn.find((p) => p.slug === enSlug);

/** Slugs EN (generateStaticParams + sitemap EN). */
export const chauffeurEnSlugs = (): string[] =>
  CHAUFFEUR_SLUG_MAP.map((m) => m.en);

export const frToEnChauffeurSlug = (fr: string): string | undefined =>
  CHAUFFEUR_SLUG_MAP.find((m) => m.fr === fr)?.en;

export const enToFrChauffeurSlug = (en: string): string | undefined =>
  CHAUFFEUR_SLUG_MAP.find((m) => m.en === en)?.fr;

/** Canonical FR d'un slug FR. */
export const chauffeurFrCanonical = (frSlug: string): string =>
  `/chauffeur/${frSlug}`;

/** Canonical EN d'un slug FR (undefined si pas de version EN). */
export const chauffeurEnCanonical = (frSlug: string): string | undefined => {
  const en = frToEnChauffeurSlug(frSlug);
  return en ? `/en/chauffeur/${en}` : undefined;
};
